/// <amd-module name="Build/Designer/EditorAuthLandingPage" />
import * as Resources from "Build/Designer/Resources";
import * as React from "react";

import { BuildRepository } from "Build/Client/Contracts/Build";
import { ILinkInfo } from "Build/SourceProviders/Common";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { ISourceProviderTile } from "Build/Designer/Wizard.types";
import { ISourceProvider } from "Build/SourceProviders/Common";

import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { getAssetLocation } from "VSS/Platform/Location";

import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";

export interface Props {
    loginUrl?: string;
    repository: BuildRepository;
    yamlFileLink: string;
    sourceProvider: ISourceProvider;
}

interface State {
    projectId: string;
    yamlLink: string;
}

export class EditorAuthLandingPage extends VssComponent<Props, State, IVssComponentContext> {
    private readonly _sourceProvider: ISourceProviderTile;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        this._sourceProvider = getSourceProviderTile(this.context.pageContext, this.props.sourceProvider.getKey()) || {} as ISourceProviderTile;

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        this.state = {
            projectId: pageData!.project!.id,
            yamlLink: this.props.yamlFileLink
        };
    }

    public componentDidMount(): void {
        if (this.state.yamlLink == null) {
            // if we don't find the yaml file (may not exist), just point the user
            // to the repository in GitHub or other external source providers
            const linkInfo = {
                branchName: this.props.repository.defaultBranch,
                repositoryName: this.props.repository.name
            } as ILinkInfo;

            this.props.sourceProvider.fetchLinks([linkInfo]).then((links) => {
                if (links && links.length > 0) {
                    const link = links[0];
                    if (link && link.repositoryLink) {
                        this.setState({yamlLink: link.repositoryLink});
                    }
                }
            });
        }
    }

    public componentWillUnmount(): void {
    }

    public render(): JSX.Element {
        const renderIcon = (sourceProvider: ISourceProviderTile): JSX.Element => {
            return sourceProvider.iconAsset ?
                <Image
                    className="source-provider-icon"
                    src={getAssetLocation(this.context.pageContext, sourceProvider.iconAsset)}
                    alt={sourceProvider.iconAssetAltText || ""} /> :
                <Icon className="source-provider-icon" iconName={this.props.sourceProvider.getRepoIconName()} />;
        }
        const hostedOn = format(Resources.PipelineHostedOn, this._sourceProvider.name);
        const authWithIdentity = format(Resources.AuthorizeWithExternalIdentity, this._sourceProvider.name);
        const viewOn = format(Resources.ViewOnExternalSite, this._sourceProvider.name);
        return <div className="ci-designer-auth-landing-page-body flex-grow flex-column justify-center flex-center">
            <div className="circle flex flex-center">
                {renderIcon(this._sourceProvider)}
            </div>
            <div className="primary-text">
                {hostedOn}
            </div>
            <div className="secondary-text">
                {authWithIdentity}
            </div>
            <Button className="authorize-button"
                primary={true}
                text={Resources.AuthorizeButtonText}
                onClick={this._createAuthRequest}
            />
            <FormatComponent format={Resources.OrFormat}>
                {<FPSLink
                    href={this.state.yamlLink}
                    telemetrySource="pipeline-editor.auth-landing-page"
                    telemetryProperties={{ clickedOn: "viewOnGitHub" }}
                    aria-label={viewOn}>
                    {viewOn}
                </FPSLink>}
            </FormatComponent>
        </div>
    }

    private _createAuthRequest = () => {
        if (this.props.loginUrl) {
            openLinkSecurely(this.props.loginUrl)
        }
    }
}

export function getSourceProviderTile(pageContext: IVssPageContext, providerKey: string): ISourceProviderTile | undefined {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const contributionMap = contributionService.getContributionsByType<ISourceProviderTile>("ms.vss-build-web.acquisition-source-provider-tile");
    const sourceProviders = Object.keys(contributionMap)
        .map(key => contributionMap[key])
        .filter((s) => s.key === providerKey);
    return sourceProviders.length > 0 ? sourceProviders[0] : undefined;
}